import { Avatar, TextField } from '@mui/material';
import {
  CalendarPickerView,
  DesktopDatePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { slashDateFormat } from 'Config';
import { he } from 'date-fns/locale';
import { useIsDesktop } from 'Hooks';
import {
  AccordionArrowBlue,
  PurpleDateIcon,
  LeftArrowDatePicker,
  RightArrowDatePicker,
} from 'Images';

interface IDatePicker {
  value: Date | null;
  label: string;
  maxDate: Date;
  minDate?: Date;
  onChange: (value: Date | null) => void;
  maxWidth?: string;
  views?: CalendarPickerView[];
}

export const DatePicker = ({
  value,
  label,
  maxDate,
  minDate,
  onChange,
  maxWidth,
  views,
}: IDatePicker) => {
  const isDesktop = useIsDesktop();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
      <DesktopDatePicker
        views={views ?? undefined}
        label={label}
        inputFormat={slashDateFormat}
        value={value}
        onChange={onChange}
        PopperProps={{
          disablePortal: isDesktop ? false : true,
        }}
        maxDate={maxDate}
        minDate={minDate}
        renderInput={(props) => (
          <TextField
            {...props}
            sx={{
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.shade_200',
              },
              '.MuiInputLabel-root': {
                fontWeight: 400,
                fontSize: '18px',
              },
              '.MuiOutlinedInput-root': {
                backgroundColor: '#fff',
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
              width: '100%',
              maxWidth: maxWidth ? maxWidth : isDesktop ? '221px' : '100%',
              flexGrow: 1,
              '& label[data-shrink="true"]': {
                mt: isDesktop ? '3px' : '5px',
              },
            }}
          />
        )}
        InputProps={{
          sx: {
            borderRadius: '6px',
            height: '48px',
            mt: '4px',
          },
        }}
        components={{
          OpenPickerIcon: (props) => (
            <Avatar
              {...props}
              src={value === null ? PurpleDateIcon : PurpleDateIcon}
              alt=''
            />
          ),
          LeftArrowIcon: (props) => (
            <Avatar
              {...props}
              src={LeftArrowDatePicker}
              alt=''
              sx={{ width: '32px', height: '32px' }}
            />
          ),
          RightArrowIcon: (props) => (
            <Avatar
              {...props}
              src={RightArrowDatePicker}
              alt=''
              sx={{ width: '32px', height: '32px' }}
            />
          ),
          SwitchViewIcon: (props) => (
            <Avatar
              {...props}
              src={AccordionArrowBlue}
              alt=''
              sx={{ width: '28px', height: '28px' }}
            />
          ),
        }}
        PaperProps={{
          sx: {
            borderRadius: '6px',
            width: isDesktop ? '400px' : undefined,
            ml: isDesktop ? '65px' : '27px',
            '.PrivatePickersFadeTransitionGroup-root, div[aria-live="polite"]':
              {
                fontSize: isDesktop ? '1.375rem' : '1.125rem',
                fontWeight: 400,
                lineHeight: isDesktop ? '1.75rem' : '1.5rem',
                color: '#1752AF',
              },
            '.MuiTypography-caption': {
              fontSize: isDesktop ? '1.125rem' : '1rem',
              fontWeight: 700,
              lineHeight: isDesktop ? '1.5rem' : '1.25rem',
              color: '#39546A',
            },
            '.MuiPickersDay-root': {
              fontSize: isDesktop ? '1.125rem' : '1rem',
              fontWeight: 400,
              lineHeight: isDesktop ? '1.625rem' : '1.375rem',
            },
            '.PrivatePickersYear-root > button': {
              fontSize: isDesktop ? '1.125rem' : '1rem',
              fontWeight: 400,
              lineHeight: isDesktop ? '1.625rem' : '1.375rem',
            },
            '.MuiPickersDay-today:not(.Mui-selected)': {
              border: 'none !important',
              backgroundColor: 'white !important',
            },
            '.Mui-selected': {
              color: 'white',
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};
